import React, { Component, PropTypes } from 'react';

// components
import Select from "react-select";
import SearchPanel from 'components/SearchPanel';
import Account from 'components/UserSelector/Account';
import LoadingIndicator from 'components/LoadingIndicator';
import InfiniteScroll from 'react-infinite-scroll-component';
import PageLoadingCircle from 'components/PageLoading/PageLoadingCircle';

// constants & util
import { roleNames } from 'constants/user';
import { areFiltersActive } from 'helpers/account';
import { isEqual } from 'lodash';
import { SEARCH_FIELD_LABEL } from 'constants/account';

export default class InternalAccounts extends Component {

    static propTypes = {
        opcoId: PropTypes.string,
        federationId: PropTypes.string,
        isHierarchical: PropTypes.bool,
        role: PropTypes.string,
        accounts: PropTypes.object,
        selectedAccount: PropTypes.shape({
            opcoId: PropTypes.string,
            customerId: PropTypes.string,
            accountId: PropTypes.string,
            accountType: PropTypes.string,
        }),
        meta: PropTypes.shape({
            offset: PropTypes.number,
            territories: PropTypes.string,
            accLoading: PropTypes.bool,
            scLoading: PropTypes.bool,
            pageLoading: PropTypes.bool,
            hasMore: PropTypes.bool,
            opcoLoading: PropTypes.bool,
        }),
        fetchAccounts: PropTypes.func,
        onSelectAccount: PropTypes.func,
        renderAccountStatBar: PropTypes.func,
        salesConsultants: PropTypes.object,
        fetchSalesConsultants: PropTypes.func,
        fetchNextPage: PropTypes.func,
        fetchOpcos: PropTypes.func,
        opcos: PropTypes.object,
        clearAccounts: PropTypes.func,
        includeInactives: PropTypes.bool,
    };

    constructor(props) {
        super(props);
        this.state = {
            filter: {
                opcoId: null,
                federationId: null,
                searchTerm: null,
            },
            dropDown: {
                opco: null,
                federation: null,
            },
            opcoList: [],
            scList: [],
            isUserSc: false,
            isFetchingNext: false,
        };
        this.accountsRef = null;
    }

    /**
     * On opco select
     * @param {Event} event event
     */
    onOpcoSelect = event => {
        const opcoId = event.value;
        if (opcoId) {
            this.setState(({ filter, dropDown }) => ({
                filter: {
                    ...filter,
                    opcoId,
                    federationId: null,
                },
                dropDown: {
                    ...dropDown,
                    opco: event,
                    federation: null,
                }
            }));
        }
    }

    /**
     * On Sales consultant select
     * @param {Event} event event
     */
    onScSelect = event => {
        const { opcos } = this.props;
        const { filter: { opcoId } } = this.state;

        const opcoData = opcos.find(element => element.opcoId === opcoId);

        this.setState(({ filter, dropDown }) => ({
            filter: {
                ...filter,
                federationId: event.value,
            },
            dropDown: {
                ...dropDown,
                opco: { value: opcoId, label: `${opcoId} - ${opcoData.opcoName}`, },
                federation: event,
            }
        }));
    }

    /**
     * Fetch next batch of accounts
     */
    onFetchNextBatch = () => {
        const { fetchNextPage, meta: { offset, territories } } = this.props;
        const { filter: { opcoId, federationId, searchTerm } } = this.state;

        this.setState({ isFetchingNext: true });
        fetchNextPage(opcoId, offset, territories, federationId, searchTerm)
            .then(data => {
                if (this.checkMultiOpco(data)) {
                    this.setState(({ dropDown }) => ({
                        dropDown: {
                            ...dropDown,
                            opco: null,
                        },
                        isFetchingNext: false
                    }));
                } else {
                    this.setState({ isFetchingNext: false });

                }
            });
    }

    /**
     * Update filter with search term
     * @param {String} actionType type of the change occured. eg: KeyChange, Search, Enter
     * @param {String} keyword text in the search box
     * @param {Boolean} updated whether the text is updated from the last time
     */
    onSearch = (actionType, keyword, updated) => {
        const respondGroup = ['Enter', 'Search'];

        if (respondGroup.includes(actionType) && updated) {
            this.setState(({ filter }) => ({
                filter: {
                    ...filter,
                    searchTerm: keyword,
                },
            }));
        }
    }

    /**
     * On clear search term
     */
    onClearSearch = () => {
        this.setState(({ filter }) => ({
            filter: {
                ...filter,
                searchTerm: null,
            },
        }));
    }

    /**
     * Refresh the search on blur
     * @param {String} keyword text in the search box
     */
    onBlurSearch = keyword => {
        this.setState(({ filter }) => ({
            filter: {
                ...filter,
                searchTerm: keyword,
            }
        }))
    }

    /**
     * Scroll the account list to the top
     */
    scrollToTop = () => {
        if (this.accountsRef) {
            this.accountsRef.scrollTo(0, 0);
        }
    }

    /**
     * Check whether the page represents multi-opco SC data
     * @param {Object} data account response data
     * @returns whether the data represents multi-opco SC
     */
    checkMultiOpco = data => this.state.filter.federationId && data && data.multiOpco;

    /**
     * render account filters
     * @returns react node
     */
    renderFilters = () => {
        const {
            isHierarchical,
            meta: {
                accLoading,
                scLoading,
                opcoLoading,
                pageLoading,
            },
        } = this.props;
        const {
            filter: { opcoId, searchTerm },
            dropDown: { opco, federation },
            opcoList,
            scList,
            isUserSc,
        } = this.state;

        return (
            <div className={'filter-area'}>
                {/* Hierarchical user's assignments are in a single opco
                Hence no need to show opco filter*/}
                {isHierarchical ?
                    null :
                    <div className={'filter-dropdown-accounts opco'}>
                        <Select
                            classNamePrefix={'no-margin opt-left ss-dropdown'}
                            options={opcoList}
                            value={opco}
                            placeholder='All Opco Sites'
                            isSearchable={true}
                            isDisabled={opcoLoading || accLoading || pageLoading}
                            onChange={this.onOpcoSelect}
                        />
                    </div>
                }
                {/* Sales Consultants can't see other Sales Consultants */}
                {isUserSc ?
                    null :
                    <div className={'filter-dropdown-accounts sc'}>
                        <Select
                            classNamePrefix={'no-margin opt-left ss-dropdown'}
                            options={scList}
                            value={federation}
                            placeholder='All Sales Consultants'
                            isSearchable={true}
                            isDisabled={!opcoId || accLoading || scLoading || opcoLoading || pageLoading}
                            onChange={this.onScSelect}
                        />
                    </div>
                }
                <div className={'accounts-searchbox'}>
                    <SearchPanel
                        onSearch={this.onSearch}
                        onClear={this.onClearSearch}
                        onBlur={this.onBlurSearch}
                        onItemClick={() => { }}
                        searchPlaceholder={SEARCH_FIELD_LABEL}
                        ingredientSearch={true}
                        disabled={accLoading || !opcoId || pageLoading}
                        keyword={searchTerm}
                    />
                </div>
            </div>
        );
    }

    /**
     * render account list
     * @returns react node
     */
    renderAccounts = () => {
        const {
            accounts,
            selectedAccount: { accountId },
            onSelectAccount,
            meta: { accLoading, hasMore }
        } = this.props;

        return (
            <div className={'accounts-list-wrapper'}>
                {accLoading ?
                    <div className={'loadmore'}><LoadingIndicator loading={accLoading} /></div> :
                    <div
                        className='accounts-list'
                        ref={ref => this.accountsRef = ref}
                    >
                        <div id='account-list' className='accounts-list-scroll'>
                            <InfiniteScroll
                                scrollableTarget='account-list'
                                dataLength={accounts.size}
                                hasMore={hasMore}
                                next={this.onFetchNextBatch}
                                loader={this.renderLoadMore()}
                            >
                                {accounts.map(account =>
                                    <Account
                                        key={account.get('accountId')}
                                        selectedAccountId={accountId}
                                        onSelect={onSelectAccount}
                                        data={account}
                                    />
                                )}
                            </InfiniteScroll>
                        </div>
                    </div>
                }
            </div>
        );
    }

    /**
     * Render load more component on scroll down
     * @returns react-node
     */
    renderLoadMore = () =>
        <div className={'loadmore'}>
            <PageLoadingCircle open />
        </div>

    componentDidUpdate(prevProps, prevState) {
        const {
            filter,
            isUserSc,
            isFetchingNext,
        } = this.state;
        const {
            fetchAccounts,
            fetchSalesConsultants,
            opcos,
            salesConsultants,
            accounts,
            meta: {
                hasMore,
                accLoading,
            },
            clearAccounts,
            includeInactives: includeInactives,
        } = this.props;

        const { filter: prevFilter } = prevState;
        const {
            opcos: prevOpcos,
            salesConsultants: prevScs,
            includeInactives: didIncludeInactives,
        } = prevProps;
        const isAccountStateChanged = includeInactives !== didIncludeInactives;

        if (!isEqual(filter, prevFilter) || isAccountStateChanged) {
            if (areFiltersActive(filter) || isAccountStateChanged) {
                fetchAccounts(filter.opcoId, filter.federationId, filter.searchTerm, includeInactives)
                    .then(data => {
                        if (this.checkMultiOpco(data)) {
                            this.setState(({ dropDown }) => ({
                                dropDown: {
                                    ...dropDown,
                                    opco: null,
                                },
                            }));
                        }
                    });
                this.scrollToTop(); // scroll to top on new data

                if (filter.opcoId !== prevFilter.opcoId && !isUserSc) {
                    fetchSalesConsultants(filter.opcoId, filter.federationId);
                }
            } else {
                // clear accounts
                clearAccounts();
            }
        }

        if (opcos && !opcos.equals(prevOpcos)) {
            const opcoList = opcos.map(opco => ({
                label: `${opco.get('opcoId')} - ${opco.get('opcoName')}`,
                value: opco.get('opcoId'),
            }));
            this.setState({
                opcoList,
            })
        }

        if (salesConsultants && !salesConsultants.equals(prevScs)) {
            const scList = salesConsultants.map(salesConsultant => ({
                label: salesConsultant.get('name'),
                value: salesConsultant.get('adId'),
            }));
            this.setState({
                scList,
            })
        }

        /**
         * If the first page data points are null
         */
        if (hasMore && !accLoading && accounts.isEmpty() && !isFetchingNext) {
            this.onFetchNextBatch();
        }
    }

    componentDidMount() {
        const {
            isHierarchical,
            federationId,
            opcoId,
            fetchOpcos,
            role,
        } = this.props;


        fetchOpcos()
            .then(data => {
                if (isHierarchical) {
                    this.setState({
                        filter: { opcoId, federationId },
                        isUserSc: role === roleNames.SALES_CONSULTANT,
                    });
                } else {
                    // set opco drop down selection once the data is received
                    const opcoData = data.find(element => element.opcoId === opcoId);
                    this.setState(({ dropDown }) => ({
                        dropDown: {
                            ...dropDown,
                            opco: { value: opcoId, label: `${opcoId} - ${opcoData.opcoName}`, },
                        },
                        filter: { opcoId },
                    }));
                }
            });
    }

    render() {
        return (
            <div className={'accounts-wrapper'}>
                {this.renderFilters()}
                {this.props.renderAccountStatBar()}
                {this.renderAccounts()}
            </div>
        )
    }
}
